import { nowMs } from "../lib/time.ts";
/**
 * THE JUPITER TRIGGER AUTHORISATION, HELD BY THE CLIENT.
 *
 * ⚠️ THE SERVER DOES NOT STORE THIS AND CANNOT RECOVER IT. Every Trigger call — craft a deposit, create an
 * order, cancel one, read the book — is authorised by a wallet-scoped JWT that JUPITER issues, not by our
 * API key: `GET /trigger/v2/vault` answers 401 with our key and no JWT. Our backend asks for the challenge
 * and exchanges the signature, then hands the token straight back; keeping it is this client's job.
 *
 * IT LIVES IN `sessionStorage`, KEYED TO ITS WALLET. A token is only ever valid for the address that signed
 * for it, so a wallet switch must not silently reuse the previous one — `tokenFor` compares the owner and
 * returns null on a mismatch rather than sending a credential that will be refused by the vendor and read
 * as our bug.
 *
 * ⚠️ EVERY ACCESSOR IS WRAPPED. `sessionStorage` THROWS outright in some contexts (a private window with
 * site data blocked, an embedded frame), and an exception on a read here would take down the trade panel
 * for a credential that is optional to have.
 */

const KEY = "kinetiq.trigger.token";

export interface TriggerAuthorisation {
  readonly owner: string;
  readonly token: string;
  readonly expiresAtMs: number;
}

/** The vendor's lifetime, measured. Held so a stale token is dropped locally rather than spending a vendor
 *  call to be told it is stale — and so the panel can say when re-authorisation is due. */
export const TRIGGER_JWT_TTL_MS = 24 * 60 * 60_000;

const store = (): Storage | null => {
  try {
    return globalThis.sessionStorage ?? null;
  } catch {
    return null;
  }
};

export function rememberTriggerToken(owner: string, token: string, expiresAtMs?: number): void {
  try {
    const held: TriggerAuthorisation = {
      owner,
      token,
      expiresAtMs: expiresAtMs ?? nowMs() + TRIGGER_JWT_TTL_MS,
    };
    store()?.setItem(KEY, JSON.stringify(held));
  } catch {
    // A wallet that cannot persist still trades for the life of the tab; it re-authorises on reload.
  }
}

export function triggerTokenFor(owner: string | null | undefined): string | null {
  if (!owner) return null;
  try {
    const raw = store()?.getItem(KEY);
    if (!raw) return null;
    const held = JSON.parse(raw) as TriggerAuthorisation;
    if (held?.owner !== owner || typeof held.token !== "string") return null;
    if (held.expiresAtMs <= nowMs()) {
      forgetTriggerToken();
      return null;
    }
    return held.token;
  } catch {
    return null;
  }
}

export function triggerAuthorisationExpiry(owner: string | null | undefined): number | null {
  if (!owner) return null;
  try {
    const raw = store()?.getItem(KEY);
    if (!raw) return null;
    const held = JSON.parse(raw) as TriggerAuthorisation;
    return held?.owner === owner ? held.expiresAtMs : null;
  } catch {
    return null;
  }
}

export function forgetTriggerToken(): void {
  try {
    store()?.removeItem(KEY);
  } catch {
    // nothing to do — a token we cannot clear expires on its own.
  }
}
